import { FunctionComponent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { clampPopoverLeft } from "@/utils/clampPopoverLeft";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

interface AnchoredPopoverProps {
	/** The trigger the popover anchors to (its bounding rect drives position). */
	anchorEl: HTMLElement | null;
	onClose: () => void;
	children: ReactNode;
	/**
	 * Portal target. The Chrome extension passes its shadow-root container so the popover renders
	 * inside the styled scope; on the web it defaults to `document.body` (web rule §11).
	 */
	portalContainer?: HTMLElement | null;
	/** Popover width in px — used for right-edge flip math. Defaults to 288 (`w-72`). */
	width?: number;
	/**
	 * Which side of the trigger the popover opens on. `"bottom"` (default) drops below the trigger;
	 * `"top"` opens upward — for triggers sitting on a bottom-sticky bar (e.g. the Inbox action bar).
	 */
	placement?: "bottom" | "top";
	/**
	 * Horizontal alignment to the trigger. `"right"` (default) puts the popover's right edge on the
	 * trigger's (it grows leftward); `"left"` aligns the left edges so it grows rightward — for
	 * triggers near the viewport's left half (e.g. the feed's jump-to-company chevron). Both clamp
	 * to the viewport.
	 */
	align?: "right" | "left";
}

/**
 * A lightweight `position: fixed` popover anchored under a trigger, portaled so it escapes the
 * panel's `overflow` clipping. Right-aligned to the trigger and flipped left when it would overflow
 * the viewport. Closes on outside click, scroll, resize and Escape. Hosts the Add-to menu and its
 * sub-pickers.
 */
export const AnchoredPopover: FunctionComponent<AnchoredPopoverProps> = ({
	anchorEl,
	onClose,
	children,
	portalContainer,
	width = 288,
	placement = "bottom",
	align = "right",
}) => {
	const popoverRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top?: number; bottom?: number; left: number } | null>(
		null
	);

	useLayoutEffect(() => {
		if (!anchorEl) {
			return;
		}

		const updatePosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			const anchoredLeft = align === "left" ? rect.left : rect.right - width;
			const left = clampPopoverLeft(anchoredLeft, width, window.innerWidth);

			if (placement === "top") {
				setPosition({ bottom: window.innerHeight - rect.top + 6, left });
			} else {
				setPosition({ top: rect.bottom + 6, left });
			}
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [anchorEl, width, placement, align]);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent) => {
			// Resolve through shadow retargeting: in the extension panel this listener sits
			// outside the shadow root, where `event.target` is the shadow host — treating
			// every inside click as "outside" and closing the menu before its click lands.
			const target = getComposedEventTarget(event);

			if (
				target &&
				!popoverRef.current?.contains(target) &&
				!anchorEl?.contains(target) &&
				// A picker may portal a MUI overlay (e.g. the sender selector) outside the popover —
				// clicks there must not close it.
				!(target instanceof HTMLElement && target.closest(".MuiModal-root, .MuiPopover-root"))
			) {
				onClose();
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [anchorEl, onClose]);

	if (!position) {
		return null;
	}

	return ReactDOM.createPortal(
		<div
			ref={popoverRef}
			className="fixed z-[2000]"
			data-testid="lead-panel-addto-popover"
			style={{ top: position.top, bottom: position.bottom, left: position.left, width }}
		>
			{children}
		</div>,
		portalContainer ?? document.body
	);
};
